import { useEffect, useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CircleHelp, Clock, TriangleAlert } from 'lucide-react';
import {
  CADENCE_MISE_A_JOUR_MIN,
  CLE_QUERY_FRAICHEUR,
  chargerEtatDonnees,
  verdictFraicheur,
} from '../../lib/fraicheur-donnees';

/**
 * « Données au HH:MM » — indicateur permanent de fraîcheur, pour TOUS les
 * comptes (l'endpoint `/api/etat-donnees` n'est pas réservé aux admins).
 *
 * L'app lit une copie locale rafraîchie toutes les ~20 min. Tant que ce
 * décalage n'était pas dit, il se lisait comme une panne : une commande créée à
 * l'instant n'apparaissait pas et paraissait avoir échoué. Annoncé, il devient
 * une information — et quand la mise à jour ne passe VRAIMENT plus,
 * l'indicateur vire à l'alerte au lieu de rester silencieusement rassurant.
 *
 * Partage de rôle avec `BacASableControl`, juste à côté (10/08) : cette
 * pastille-ci est la SEULE à dire « Données au … » (l'état des données) ; le
 * contrôle bac à sable, lui, ne garde que son geste (« Bac à sable », le bouton
 * qui recharge la copie locale depuis la production). Deux « Données au » côte à
 * côte, sur deux horloges différentes, auraient été pires que pas d'indicateur.
 */
const POLL_MS = 60_000;
/** Le temps passe même sans nouvelle réponse : l'âge affiché doit vieillir tout seul. */
const TIC_HORLOGE_MS = 30_000;

export function FraicheurDonnees(): JSX.Element | null {
  // Deux façons d'ouvrir le détail, volontairement distinctes : le SURVOL (ce
  // que Ben a demandé, souris) et le CLIC qui l'épingle (tactile et clavier,
  // où il n'y a pas de survol). Un simple `toggle` au clic refermait le panneau
  // que le survol venait d'ouvrir — un clic qui ferme ce qu'on veut lire.
  const [survol, setSurvol] = useState(false);
  const [epingle, setEpingle] = useState(false);
  const ouvert = survol || epingle;
  const [, setTic] = useState(0);
  const panneauId = useId();
  const conteneurRef = useRef<HTMLDivElement>(null);

  const { data: etat = null } = useQuery({
    queryKey: CLE_QUERY_FRAICHEUR,
    queryFn: ({ signal }) => chargerEtatDonnees(signal),
    retry: false,
    refetchInterval: POLL_MS,
    staleTime: 30_000,
  });

  useEffect(() => {
    const t = setInterval(() => setTic((n) => n + 1), TIC_HORLOGE_MS);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!ouvert) return undefined;
    const surTouche = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setEpingle(false);
        setSurvol(false);
      }
    };
    const surClicAilleurs = (e: MouseEvent): void => {
      if (!conteneurRef.current?.contains(e.target as Node)) setEpingle(false);
    };
    document.addEventListener('keydown', surTouche);
    document.addEventListener('mousedown', surClicAilleurs);
    return () => {
      document.removeEventListener('keydown', surTouche);
      document.removeEventListener('mousedown', surClicAilleurs);
    };
  }, [ouvert]);

  // Endpoint absent (404), refusé (403) ou serveur injoignable : pas de pastille
  // du tout — mieux vaut rien qu'un indicateur qui invente une heure.
  if (!etat) return null;

  const v = verdictFraicheur(etat);
  const alerte = v.ton === 'alerte';
  const inconnu = v.ton === 'inconnu';

  return (
    <div
      ref={conteneurRef}
      className="relative shrink-0"
      onMouseEnter={() => setSurvol(true)}
      onMouseLeave={() => setSurvol(false)}
    >
      <button
        type="button"
        data-testid="fraicheur-bouton"
        data-ton={v.ton}
        aria-expanded={ouvert}
        aria-controls={panneauId}
        // En téléphone la pastille se réduit à son icône : le libellé n'a plus
        // de support visible, il passe donc par ici (et par le panneau au clic).
        aria-label={`${v.titre} — voir le détail`}
        onClick={() => setEpingle((e) => !e)}
        onFocus={() => setSurvol(true)}
        onBlur={() => setSurvol(false)}
        className={[
          'h-8 inline-flex items-center gap-1.5 rounded-md border px-2 text-[12px] font-medium max-w-[46vw] sm:max-w-none',
          'transition-colors',
          alerte
            ? 'border-red-200 bg-[var(--danger-soft)] text-[var(--danger-fg)] hover:bg-red-100'
            : inconnu
              ? 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
              : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900',
        ].join(' ')}
      >
        {alerte ? (
          <TriangleAlert size={14} className="shrink-0" aria-hidden="true" />
        ) : inconnu ? (
          <CircleHelp size={14} className="shrink-0" aria-hidden="true" />
        ) : (
          <Clock size={14} className="shrink-0" aria-hidden="true" />
        )}
        {/* Trois largeurs, mesurées à 390 px avant d'être écrites : le libellé
            complet dès qu'il y a la place, l'heure seule au milieu, et l'icône
            seule en téléphone — où la barre du haut est déjà pleine et où
            ajouter 90 px de texte faisait déborder la page (constaté à 440 px
            de contenu pour 375 de large, corrigé ici). L'alerte, elle, reste
            visible à toutes les tailles : c'est la couleur et l'icône qui la
            portent, le mot est à un doigt. */}
        <span className="hidden lg:inline whitespace-nowrap">{v.libelle}</span>
        <span className="hidden sm:inline lg:hidden truncate">{v.libelleCompact}</span>
      </button>

      {ouvert && (
        <div
          id={panneauId}
          role="tooltip"
          data-testid="fraicheur-panneau"
          /* Ancrage sous la pastille en grand écran ; en téléphone la pastille
             se retrouve à gauche de la barre et un panneau aligné sur elle
             sortait de l'écran par la gauche (moitié du texte coupée, mesuré à
             390 px). Il s'accroche donc à la FENÊTRE sous le header, avec la
             même gouttière des deux côtés. */
          className="z-50 rounded-lg border border-slate-200 bg-white p-3 text-left shadow-xl max-sm:fixed max-sm:left-3 max-sm:right-3 max-sm:top-[3.25rem] sm:absolute sm:right-0 sm:top-full sm:mt-1.5 sm:w-80"
        >
          <p className={`text-[13px] font-semibold ${alerte ? 'text-[var(--danger-fg)]' : 'text-slate-900'}`}>
            {v.titre}
          </p>
          <p className="mt-1 text-[12.5px] leading-snug text-slate-600">{v.phrase}</p>

          {v.heure && (
            <p className="mt-2 text-[12px] text-slate-500">
              Dernière mise à jour : <span className="font-medium text-slate-700">{v.heure}</span>
              {v.ageTexte ? ` (${v.ageTexte})` : ''}
            </p>
          )}

          {etat.enErreur.length > 0 && (
            <div className="mt-2 border-t border-slate-100 pt-2">
              <p className="text-[12px] font-medium text-[var(--danger-fg)]">Ne se met plus à jour :</p>
              <ul className="mt-1 space-y-1">
                {etat.enErreur.map((r) => (
                  <li key={r.cle} className="text-[12px] text-slate-600">
                    <span className="font-medium text-slate-800">{r.libelle}</span>
                    {r.message ? <span className="block text-[11px] text-slate-400 break-words">{r.message}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {etat.enRetard.length > 0 && (
            <div className="mt-2 border-t border-slate-100 pt-2">
              <p className="text-[12px] font-medium text-[var(--danger-fg)]">N'a pas été rafraîchi à sa cadence prévue :</p>
              <ul className="mt-1 space-y-1">
                {etat.enRetard.map((r) => (
                  <li key={r.cle} className="text-[12px] text-slate-600">
                    <span className="font-medium text-slate-800">{r.libelle}</span>
                    {r.message ? <span className="block text-[11px] text-slate-400 break-words">{r.message}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {etat.aActiver.length > 0 && (
            <div className="mt-2 border-t border-slate-100 pt-2" data-testid="fraicheur-a-activer">
              <p className="text-[12px] font-medium text-[var(--warn-fg)]">À activer :</p>
              <ul className="mt-1 space-y-1">
                {etat.aActiver.map((r) => (
                  <li key={r.cle} className="text-[12px] text-slate-600">
                    <span className="font-medium text-[var(--warn-fg)]">{r.libelle}</span>
                    {r.message ? <span className="block text-[11px] text-slate-400 break-words">{r.message}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="mt-2 border-t border-slate-100 pt-2 text-[11.5px] text-slate-400">
            Ces données se mettent à jour automatiquement, environ toutes les {CADENCE_MISE_A_JOUR_MIN} minutes.
          </p>
        </div>
      )}
    </div>
  );
}
